import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../AuthContext';
import "./AdminCatalogue.css";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminCatalogue() {
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [catalogues, setCatalogues] = useState([]);
  const [filteredCatalogues, setFilteredCatalogues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [newCatalogue, setNewCatalogue] = useState({
    name: "",
    description: "",
    file: null
  });

  useEffect(() => {
    const fetchCatalogues = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/Catalogues`);
        if (!response.ok) {
          throw new Error("Failed to fetch catalogues");
        }
        const data = await response.json();
        setCatalogues(data);
        setFilteredCatalogues(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCatalogues();
  }, []);

  useEffect(() => {
    let result = [...catalogues];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(catalogue =>
        (catalogue.name && catalogue.name.toLowerCase().includes(search)) ||
        (catalogue.description && catalogue.description.toLowerCase().includes(search)) ||
        (catalogue.fileName && catalogue.fileName.toLowerCase().includes(search))
      );
    }

    // Date range filter
    if (dateFilter !== 'all') {
      const today = new Date();
      let cutoffDate;

      if (dateFilter === 'last30') {
        cutoffDate = new Date();
        cutoffDate.setDate(today.getDate() - 30);
      } else if (dateFilter === 'last60') {
        cutoffDate = new Date();
        cutoffDate.setDate(today.getDate() - 60);
      }

      if (cutoffDate) {
        result = result.filter(catalogue => {
          const uploadDate = new Date(catalogue.uploadDate);
          return uploadDate >= cutoffDate;
        });
      }
    }

    setFilteredCatalogues(result);
  }, [searchTerm, dateFilter, catalogues]);

   const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; 
        } catch (e) {
            return dateString;
        }
    };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCatalogue(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Check if file size exceeds 200MB
      const maxSize = 200 * 1024 * 1024;

      if (file.size > maxSize) {
        Swal.fire({
          title: 'Error!',
          text: 'File size exceeds the maximum limit of 200MB.',
          icon: 'error',
          confirmButtonColor: '#45a049'
        });

        e.target.value = '';
        return;
      }

      setNewCatalogue(prevState => ({
        ...prevState,
        file: file
      }));
    }
  };

  const uploadFileInChunks = async (file) => {
    // Keep each request small enough to clear IIS / proxy body limits. At 50MB a
    // 45MB file was sent as ONE request, which defeated the point of chunking.
    const chunkSize = 5 * 1024 * 1024; // 5MB
    const chunks = Math.ceil(file.size / chunkSize);
    const fileId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;

    try {
      // Upload chunks
      for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const chunkFormData = new FormData();
        chunkFormData.append('chunk', chunk);
        chunkFormData.append('chunkIndex', chunkIndex);
        chunkFormData.append('totalChunks', chunks);
        chunkFormData.append('fileId', fileId);
        chunkFormData.append('fileName', file.name);

        // authFetch: the endpoint is [Authorize(Roles = "admin, warehouse, sales")]
        // and JwtMiddleware only reads the Authorization header
        const response = await authFetch(API_BASE_URL +'/api/Catalogues/chunk', {
          method: 'POST',
          body: chunkFormData
        });

        if (!response.ok) {
          throw new Error(
            `Chunk ${chunkIndex + 1}/${chunks} failed (HTTP ${response.status})`
          );
        }

        // Update progress
       // const progressPercent = ((chunkIndex + 1) / chunks) * 100;
        //setProgress(progressPercent);
        //setUploadStatus(`Uploading: ${Math.round(progressPercent)}% (Chunk ${chunkIndex + 1}/${chunks})`);
      }

      // Finalize upload
      const finalizeResponse = await authFetch(API_BASE_URL +'/api/Catalogues/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId,
          fileName: file.name,
          totalChunks: chunks
        })
      });

      if (!finalizeResponse.ok) {
        throw new Error('Failed to finalize upload');
      }

      const result = await finalizeResponse.json();
      return result;

    } catch (error) {
      // Cleanup on error
      await authFetch(API_BASE_URL +'/api/Catalogues/cleanup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId })
      });
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newCatalogue.name || !newCatalogue.file) {
      setError("Please fill in all required fields and upload a file.");
      return;
    }

    setIsLoading(true);
    

    try {
      
       // Upload file in chunks 
      const uploadResult = await uploadFileInChunks(newCatalogue.file);


      const formData = new FormData();
      formData.append("name", newCatalogue.name);
      formData.append("description", newCatalogue.description);


      formData.append("fileId", uploadResult.fileId);
      formData.append("fileName", uploadResult.fileName);
      formData.append("filePath",  uploadResult.filePath);
      formData.append("fileSize",  uploadResult.fileSize);

      const response = await authFetch(`${API_BASE_URL}/api/Catalogues`, {
        method: "POST",
        body: formData,
        credentials: "include"
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.title || errorData.message || "Failed to upload catalogue";

          if (errorData.errors) {
            const validationErrors = Object.entries(errorData.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('; ');

            errorMessage += ` - ${validationErrors}`;
          }
        } catch {
          errorMessage = errorText || "Failed to upload catalogue";
        }
        throw new Error(errorMessage);
      }

      const newUploadedCatalogue = await response.json();

      setCatalogues(prevCatalogues => [...prevCatalogues, newUploadedCatalogue]);
      setFilteredCatalogues(prevCatalogues => [...prevCatalogues, newUploadedCatalogue]);
      setNewCatalogue({
        name: "",
        description: "",
        file: null
      });
      setShowModal(false);
      setError(null);
      Swal.fire({
        title: 'Success!',
        text: 'Catalogue has been uploaded successfully.',
        icon: 'success',
        timer: 1500,
        confirmButtonColor: '#45a049'
      });
    } catch (err) {
      setError(err.message);
      Swal.fire({
        title: 'Error!',
        text: `Failed to upload catalogue: ${err.message}`,
        icon: 'error',
        confirmButtonColor: '#45a049'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#45a049',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    });
    if (!result.isConfirmed) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await authFetch(`${API_BASE_URL}/api/Catalogues/${id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error("Failed to delete catalogue");
      }

      setCatalogues(prevCatalogues =>
        prevCatalogues.filter(catalogue => catalogue.id !== id)
      );
      setFilteredCatalogues(prevCatalogues =>
        prevCatalogues.filter(catalogue => catalogue.id !== id)
      );

      Swal.fire(
        'Deleted!',
        'Catalogue has been deleted successfully.',
        'success'
      );
    } catch (err) {
      setError(err.message);
      Swal.fire(
        'Error!',
        `Failed to delete catalogue: ${err.message}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setDateFilter('all');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  const renderTableActions = (catalogue) => {
    return (
      <div className="admin-catalogue-table-actions">
        <a
          href={`${catalogue.downloadUrl}`}
          className="admin-catalogue-btn-action admin-catalogue-btn-download"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fas fa-download"></i> Download
        </a>
        <button
          onClick={() => handleDelete(catalogue.id)}
          className="admin-catalogue-btn-action admin-catalogue-btn-delete"
        >
          <i className="fas fa-trash"></i> Delete
        </button>
      </div>
    );
  };

  return (
    <div className="admin-catalogue-container">
      {/* Back Dashboard Section */}
      <div className="admin-catalogue-header">
        <button
          className="admin-catalogue-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>
      <div className="admin-catalogue-title">
        <h1>Catalogue Management</h1>
      </div>
      {error && (
        <div className="admin-catalogue-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Upload Button */}
      <div className="admin-catalogue-actions">
        <button
          onClick={() => setShowModal(true)}
          className="admin-catalogue-upload-btn"
        >
          <i className="fas fa-upload"></i> Create New Catalogue
        </button>
      </div>

      {/* Filter Section */}
      <div className="admin-catalogue-filter">
        <div className="admin-catalogue-filter-group">
          <label>Search</label>

          <input
            type="text"
            placeholder="Search by name, description, filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-catalogue-filter-group">
          <label>Date Range</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="last30">Last 30 Days</option>
            <option value="last60">Last 60 Days</option>
          </select>
        </div>
        <button
          className="admin-catalogue-filter-btn"
          onClick={resetFilters}
        >
          Search
        </button>
      </div>

      {/* Catalogues Table Section */}
      {isLoading && (
        <div className="admin-catalogue-loading">
          <i className="fas fa-spinner fa-spin"></i> Loading catalogues...
        </div>
      )}

      {!isLoading && filteredCatalogues.length === 0 && (
        <div className="admin-catalogue-empty">
          <i className="fas fa-book"></i>
          <p>No catalogues found matching your criteria.</p>
        </div>
      )}

      {!isLoading && filteredCatalogues.length > 0 && (
        <div className="admin-catalogue-table-container">
          <table className="admin-catalogue-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th> 
                <th>Size</th>
                <th>Upload Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCatalogues.map((catalogue) => (
                <tr key={catalogue.id}>
                  <td>{catalogue.name}</td>
                  <td>{catalogue.description}</td> 
                  <td>{formatFileSize(catalogue.fileSize)}</td>
                  <td>
                    {formatDate(catalogue.uploadDate)}
                  </td>
                  <td>
                    {renderTableActions(catalogue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Upload Modal Section */}
      {showModal && (
        <div className="admin-catalogue-modal-overlay">
          <div className="admin-catalogue-modal">
            <div className="admin-catalogue-modal-header">
              <h2>Upload New Catalogue</h2>
              <button
                className="admin-catalogue-modal-close"
                onClick={() => setShowModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="admin-catalogue-modal-body">
              <p className="admin-catalogue-section-message">
                Add a new catalogue for customers to download. Only PDF files are supported.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="admin-catalogue-form-group">
                  <label htmlFor="name">
                    Catalogue Name
                    <span className="admin-catalogue-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={newCatalogue.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="admin-catalogue-form-group">
                  <label htmlFor="description">Description
                    <span className="admin-catalogue-required">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={newCatalogue.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="admin-catalogue-form-group">
                  <label htmlFor="catalogueFileInput">
                    Catalogue File (PDF)
                    <span className="admin-catalogue-required">*</span>
                  </label>
                  <input
                    type="file"
                    id="catalogueFileInput"
                    accept=".pdf"
                    onChange={handleFileChange}
                    required
                  />
                  {newCatalogue.file && (
                    <p className="admin-catalogue-file-info">
                      Selected file: {newCatalogue.file.name} ({formatFileSize(newCatalogue.file.size)})
                    </p>
                  )}
                </div>
                <div className="admin-catalogue-modal-footer">
                  <button
                    type="button"
                    className="admin-catalogue-btn-cancel"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="admin-catalogue-btn-submit"
                  >
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Uploading...
                      </>
                    ) : (
                      <>
                        Upload Catalogue
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCatalogue;